import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useSocialService } from '../../services/socialService';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AddFriendModal from './components/AddFriendModal';
import TaskShareModal from './components/TaskShareModal';
import ShareBookModal from './components/ShareBookModal';
import ShareHabitModal from './components/ShareHabitModal';
import ShareMindfulnessModal from './components/ShareMindfulnessModal';
import './SocialPage.css';

const ShareIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/>
    <circle cx="6" cy="12" r="3"/>
    <circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

const TaskIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11l3 3L22 4"/>
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
  </svg>
);

const MoodIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
    <line x1="9" y1="9" x2="9.01" y2="9"/>
    <line x1="15" y1="9" x2="15.01" y2="9"/>
  </svg>
);

const JournalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <line x1="10" y1="9" x2="8" y2="9"/>
  </svg>
);

const MindfulnessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22c6.23-.05 11.25-7.3 9.17-12.5"/>
    <path d="M8.56 2.75c4.37 6 6 9.42 8 17.72"/>
    <path d="M2 17.66c7.84-7.84 13-8 21.56-15.5"/>
  </svg>
);

const HabitIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22c6.23-.05 11.25-7.3 9.17-12.5"/>
    <path d="M2 2l20 20"/>
    <path d="M12 13V7"/>
    <path d="M12 17h.01"/>
  </svg>
);

const BookIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);

const SkillIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
  </svg>
);

const SocialPage = () => {
  const [user] = useAuthState(auth);
  const { 
    friends,
    messages,
    pendingRequests,
    subscribeToMessages,
    subscribeToFriends,
    sendMessage,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    getUserTasks,
    shareTask,
    shareBook,
    shareHabit,
    shareMindfulness,
    updateOnlineStatus
  } = useSocialService();
  
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAddFriendModalOpen, setIsAddFriendModalOpen] = useState(false);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [isTaskShareModalOpen, setIsTaskShareModalOpen] = useState(false);
  const [userTasks, setUserTasks] = useState([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isShareBookModalOpen, setIsShareBookModalOpen] = useState(false);
  const [isShareHabitModalOpen, setIsShareHabitModalOpen] = useState(false);
  const [isShareMindfulnessModalOpen, setIsShareMindfulnessModalOpen] = useState(false);

  // Subscribe to messages when a friend is selected
  useEffect(() => {
    if (!selectedFriend || !user) return;
    console.log('Setting up message subscription for friend:', selectedFriend);
    const unsubscribe = subscribeToMessages(user.uid, selectedFriend.userId);
    return () => {
      console.log('Cleaning up message subscription');
      unsubscribe();
    };
  }, [selectedFriend, user, subscribeToMessages]);

  // Subscribe to friends and friend requests
  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeToFriends(user.uid, user.email);
    return () => unsubscribe();
  }, [user, subscribeToFriends]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const messagesContainer = document.querySelector('.messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }, [messages]);

  // Update online status when component mounts/unmounts
  useEffect(() => {
    if (!user) return;

    // Set online when component mounts
    updateOnlineStatus(user.uid, true);

    // Set offline when component unmounts
    return () => {
      updateOnlineStatus(user.uid, false);
    };
  }, [user, updateOnlineStatus]);

  // Format last seen time
  const formatLastSeen = (timestamp) => {
    if (!timestamp) return '';
    
    const lastSeen = timestamp.toDate();
    const now = new Date();
    const diffMinutes = Math.floor((now - lastSeen) / (1000 * 60));
    
    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return lastSeen.toLocaleDateString();
  };

  const handleAddFriend = async (email) => {
    if (!user) return false;
    
    setIsLoading(true);
    setError('');
    try {
      await sendFriendRequest(user.uid, email);
      return true;
    } catch (error) {
      setError(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      await acceptFriendRequest(requestId, user.uid);
    } catch (error) {
      console.error('Error accepting friend request:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectRequest = async (requestId) => {
    setIsLoading(true);
    try {
      await rejectFriendRequest(requestId);
    } catch (error) {
      console.error('Error rejecting friend request:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedFriend || !user || isLoading) return;

    setIsLoading(true);
    try {
      await sendMessage(user.uid, selectedFriend.userId, newMessage);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      // Show error to user
      setError('Failed to send message. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const messageDate = new Date(timestamp);
    const now = new Date();
    const isToday = messageDate.toDateString() === now.toDateString();
    
    if (isToday) {
      return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return messageDate.toLocaleDateString([], { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleShareMore = () => {
    setIsShareMenuOpen(prev => !prev);
  };

  const handleShareOption = async (type) => {
    setIsShareMenuOpen(false);
    
    if (type === 'tasks') {
      setIsLoadingTasks(true);
      try {
        const tasks = await getUserTasks(user.uid);
        setUserTasks(tasks);
        setIsTaskShareModalOpen(true);
      } catch (error) {
        console.error('Error fetching tasks:', error);
        setError('Failed to load tasks. Please try again.');
      } finally {
        setIsLoadingTasks(false);
      }
    } else if (type === 'books') {
      setIsShareBookModalOpen(true);
    } else if (type === 'habits') {
      setIsShareHabitModalOpen(true);
    } else if (type === 'mindfulness') {
      setIsShareMindfulnessModalOpen(true);
    }
    // Handle other share types here
  };

  const handleShareTask = async (task) => {
    if (!selectedFriend || !user) return;
    
    setIsLoading(true);
    try {
      await shareTask(user.uid, selectedFriend.userId, task);
      setIsTaskShareModalOpen(false);
    } catch (error) {
      console.error('Error sharing task:', error);
      setError('Failed to share task. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareBook = async (book) => {
    if (!selectedFriend || !user) return;
    
    setIsLoading(true);
    try {
      await shareBook(user.uid, selectedFriend.userId, book);
      setIsShareBookModalOpen(false);
    } catch (error) {
      console.error('Error sharing book:', error);
      setError('Failed to share book. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareHabit = async (habit) => {
    if (!selectedFriend || !user) return;
    
    setIsLoading(true);
    try {
      await shareHabit(user.uid, selectedFriend.userId, habit);
      setIsShareHabitModalOpen(false);
    } catch (error) {
      console.error('Error sharing habit:', error);
      setError('Failed to share habit. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareMindfulness = async (session) => {
    if (!selectedFriend || !user) return;
    
    setIsLoading(true);
    try {
      await shareMindfulness(user.uid, selectedFriend.userId, session);
      setIsShareMindfulnessModalOpen(false);
    } catch (error) {
      console.error('Error sharing mindfulness session:', error);
      setError('Failed to share mindfulness session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="social-container">
          <p>Please sign in to use the chat feature.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="social-container">
        <motion.div 
          className="page-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1>Social</h1>
          <p>Connect and track your relationships</p>
        </motion.div>

        <motion.div 
          className="social-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="social-layout">
            <div className="friends-section">
              <div className="friends-header">
                <h3>Friends</h3>
                <button 
                  className="add-friend-button"
                  onClick={() => setIsAddFriendModalOpen(true)}
                >
                  Add Friend
                </button>
              </div>

              {pendingRequests.length > 0 && (
                <motion.div 
                  className="friend-requests"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                >
                  <h3>Friend Requests</h3>
                  <AnimatePresence>
                    {pendingRequests.map((request) => (
                      <motion.div
                        key={request.id}
                        className="friend-request-item"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                      >
                        <div className="request-info">
                          <div className="friend-avatar">
                            {request.email?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <span>{request.email || 'Unknown User'}</span>
                        </div>
                        <div className="request-actions">
                          <button
                            className="accept-btn"
                            onClick={() => handleAcceptRequest(request.id)}
                            disabled={isLoading}
                          >
                            Accept
                          </button>
                          <button
                            className="reject-btn"
                            onClick={() => handleRejectRequest(request.id)}
                            disabled={isLoading}
                          >
                            Reject
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </motion.div>
              )}
              
              <div className="friends-list">
                {friends.map((friend) => (
                  <motion.div
                    key={friend.id}
                    className={`friend-item ${selectedFriend?.id === friend.id ? 'selected' : ''}`}
                    onClick={() => setSelectedFriend(friend)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="friend-avatar">
                      {friend.email ? friend.email.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div className="friend-info">
                      <h3>{friend.email ? friend.email.split('@')[0] : 'Unknown'}</h3>
                      <span className={`status ${friend.status}`}>
                        {friend.status === 'online' ? 'online' : 
                         friend.lastChanged ? `last seen ${formatLastSeen(friend.lastChanged)}` : 
                         'offline'}
                      </span>
                    </div>
                    {friend.status === 'online' && (
                      <div className="online-indicator" />
                    )}
                  </motion.div>
                ))}
                {friends.length === 0 && (
                  <p className="no-friends">No friends yet. Add some friends to start chatting!</p>
                )}
              </div>
            </div>

            <div className="chat-section">
              {selectedFriend ? (
                <>
                  <div className="chat-header">
                    <h2>Chat with {selectedFriend.email ? selectedFriend.email.split('@')[0] : 'Unknown'}</h2>
                    <div style={{ position: 'relative' }}>
                      <button className="share-more-btn" onClick={handleShareMore}>
                        <ShareIcon /> Share More
                      </button>
                      {isShareMenuOpen && (
                        <>
                          <motion.div 
                            className="share-dropdown"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.1 }}
                          >
                            <motion.div 
                              className="share-dropdown-item tasks"
                              onClick={() => handleShareOption('tasks')}
                              whileHover={{ x: 4 }}
                            >
                              <TaskIcon />
                              <span>Share Task</span>
                            </motion.div>
                            <motion.div 
                              className="share-dropdown-item moods"
                              onClick={() => handleShareOption('moods')}
                              whileHover={{ x: 4 }}
                            >
                              <MoodIcon />
                              <span>Share Mood</span>
                            </motion.div>
                            <motion.div 
                              className="share-dropdown-item journal"
                              onClick={() => handleShareOption('journal')}
                              whileHover={{ x: 4 }}
                            >
                              <JournalIcon />
                              <span>Share Journal Entry</span>
                            </motion.div>
                            <motion.div 
                              className="share-dropdown-item mindfulness"
                              onClick={() => handleShareOption('mindfulness')}
                              whileHover={{ x: 4 }}
                            >
                              <MindfulnessIcon />
                              <span>Share Mindfulness Session</span>
                            </motion.div>
                            <motion.div 
                              className="share-dropdown-item habits"
                              onClick={() => handleShareOption('habits')}
                              whileHover={{ x: 4 }}
                            >
                              <HabitIcon />
                              <span>Share Habit</span>
                            </motion.div>
                            <motion.div 
                              className="share-dropdown-item books"
                              onClick={() => handleShareOption('books')}
                              whileHover={{ x: 4 }}
                            >
                              <BookIcon />
                              <span>Share Book</span>
                            </motion.div>
                            <motion.div 
                              className="share-dropdown-item skills"
                              onClick={() => handleShareOption('skills')}
                              whileHover={{ x: 4 }}
                            >
                              <SkillIcon />
                              <span>Share Skill</span>
                            </motion.div>
                          </motion.div>
                          <div className="share-overlay" onClick={() => setIsShareMenuOpen(false)} />
                        </>
                      )}
                    </div>
                  </div>
                  <div className="messages-container">
                    <AnimatePresence initial={false}>
                      {messages.map((message) => (
                        <div key={message.id} className="message-wrapper">
                          <motion.div
                            className={`message ${message.sender === 'me' ? 'me' : 'other'}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            layout
                          >
                            {message.type === 'book' ? (
                              <div className="book-message">
                                <div className="book-preview">
                                  {message.content.coverUrl && (
                                    <div className="book-cover">
                                      <img src={message.content.coverUrl} alt={message.content.title} />
                                    </div>
                                  )}
                                  <div className="book-preview-info">
                                    <h4>{message.content.title}</h4>
                                    <p className="book-author">by {message.content.author}</p>
                                    <p className="book-status">Status: {message.content.status}</p>
                                    {message.content.progress !== undefined && message.content.pages && (
                                      <div className="book-progress">
                                        <div className="progress-bar">
                                          <div 
                                            className="progress-fill"
                                            style={{ width: `${(message.content.progress / message.content.pages) * 100}%` }}
                                          />
                                        </div>
                                        <p className="progress-text">
                                          Progress: {message.content.progress} / {message.content.pages} pages
                                        </p>
                                      </div>
                                    )}
                                    {message.content.rating && (
                                      <p className="book-rating">
                                        Rating: {Array(message.content.rating).fill('⭐').join('')}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : message.type === 'task' ? (
                              <div className="task-message">
                                <div className="task-preview">
                                  <div className="task-preview-info">
                                    <h4>{message.taskData.title}</h4>
                                    {message.taskData.description && (
                                      <p className="task-description">{message.taskData.description}</p>
                                    )}
                                    <div className="task-meta">
                                      <span className="due-date">
                                        {message.taskData.dueDate ? new Date(message.taskData.dueDate).toLocaleDateString() : 'No due date'}
                                      </span>
                                      <span className={`task-status ${message.taskData.status?.toLowerCase() || 'not-started'}`}>
                                        {message.taskData.status || 'Not started'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : message.type === 'habit' ? (
                              <div className="habit-message">
                                <div className="habit-preview">
                                  <div className="habit-preview-info">
                                    <h4>{message.content.name}</h4>
                                    {message.content.streak > 0 && (
                                      <div className="habit-streak">
                                        {message.content.streak} day streak
                                      </div>
                                    )}
                                    {message.content.completedDates?.length > 0 && (
                                      <div className="habit-dates">
                                        {message.content.completedDates.slice(-5).map((date, index) => (
                                          <div key={index} className="habit-date">
                                            {new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : message.type === 'mindfulness' ? (
                              <div className="mindfulness-message">
                                <div className="mindfulness-preview">
                                  <div className="mindfulness-preview-info">
                                    <h4>{message.content.type}</h4>
                                    <div className="mindfulness-meta">
                                      <span className="session-duration">
                                        ⏱️ {message.content.duration >= 60 
                                          ? `${Math.floor(message.content.duration / 60)}h ${message.content.duration % 60}m`
                                          : `${message.content.duration}m`}
                                      </span>
                                      <span className="session-date">
                                        📅 {new Date(message.content.completedAt).toLocaleDateString()}
                                      </span>
                                    </div>
                                    {message.content.mood && (
                                      <div className="session-mood">
                                        Mood after: {message.content.mood}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <p>{message.text}</p>
                            )}
                          </motion.div>
                          <motion.div 
                            className={`message-footer ${message.sender === 'me' ? 'me' : 'other'}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1 }}
                          >
                            <span className="timestamp">{formatMessageTime(message.timestamp)}</span>
                            {message.sender === 'me' && (
                              <span className={`message-status ${message.status}`} />
                            )}
                          </motion.div>
                        </div>
                      ))}
                    </AnimatePresence>
                    {messages.length === 0 && (
                      <div className="no-messages">
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    )}
                  </div>
                  <form className="message-form" onSubmit={handleSendMessage}>
                    <input
                      type="text"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={isLoading}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e);
                        }
                      }}
                    />
                    <button type="submit" disabled={isLoading || !newMessage.trim()}>
                      {isLoading ? 'Sending...' : 'Send'}
                    </button>
                  </form>
                </>
              ) : (
                <div className="no-chat-selected">
                  <p>Select a friend to start chatting</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        <AddFriendModal
          isOpen={isAddFriendModalOpen}
          onClose={() => setIsAddFriendModalOpen(false)}
          onAddFriend={handleAddFriend}
          isLoading={isLoading}
          error={error}
        />

        <TaskShareModal
          isOpen={isTaskShareModalOpen}
          onClose={() => setIsTaskShareModalOpen(false)}
          onShareTask={handleShareTask}
          tasks={userTasks}
          isLoading={isLoading}
        />

        <ShareBookModal
          isOpen={isShareBookModalOpen}
          onClose={() => setIsShareBookModalOpen(false)}
          onShare={handleShareBook}
          userId={user?.uid}
        />

        <ShareHabitModal
          isOpen={isShareHabitModalOpen}
          onClose={() => setIsShareHabitModalOpen(false)}
          onShare={handleShareHabit}
          userId={user?.uid}
        />

        <ShareMindfulnessModal
          isOpen={isShareMindfulnessModalOpen}
          onClose={() => setIsShareMindfulnessModalOpen(false)}
          onShare={handleShareMindfulness}
          userId={user?.uid}
        />
      </div>
    </DashboardLayout>
  );
};

export default SocialPage; 